import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchAdminActionAdminsAPI,
  fetchAdminActionLogsAPI,
  fetchAdminActionTournamentsAPI,
  fetchAdminActionTypesAPI,
} from "./adminActionLogsAPI";

const initialState = {
  items: [],
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasMore: false,
  },
  fetchStatus: "idle",
  fetchActionTypesStatus: "idle",
  fetchActionAdminsStatus: "idle",
  fetchActionTournamentsStatus: "idle",
  actionTypes: [],
  admins: [],
  tournaments: {
    items: [],
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 1,
      hasMore: false,
    },
  },
  error: null,
};

export const fetchAdminActionLogs = createAsyncThunk(
  "adminActionLogs/fetch",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await fetchAdminActionLogsAPI(params);
      return response?.data || {};
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch admin action logs",
      );
    }
  },
);

export const fetchAdminActionTypes = createAsyncThunk(
  "adminActionLogs/fetchActionTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchAdminActionTypesAPI();
      return Array.isArray(response?.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch admin action types",
      );
    }
  },
);

export const fetchAdminActionAdmins = createAsyncThunk(
  "adminActionLogs/fetchActionAdmins",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchAdminActionAdminsAPI();
      return Array.isArray(response?.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch admin action admins",
      );
    }
  },
);

export const fetchAdminActionTournaments = createAsyncThunk(
  "adminActionLogs/fetchActionTournaments",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await fetchAdminActionTournamentsAPI(params);
      return response?.data || {};
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch admin action tournaments",
      );
    }
  },
);

const adminActionLogsSlice = createSlice({
  name: "adminActionLogs",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminActionLogs.pending, (state) => {
        state.fetchStatus = "loading";
        state.error = null;
      })
      .addCase(fetchAdminActionLogs.fulfilled, (state, action) => {
        state.fetchStatus = "succeeded";
        state.items = Array.isArray(action.payload?.items) ? action.payload.items : [];
        state.pagination = {
          ...state.pagination,
          ...(action.payload?.pagination || {}),
        };
      })
      .addCase(fetchAdminActionLogs.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.error = action.payload || "Failed to fetch admin action logs";
      })
      .addCase(fetchAdminActionTypes.pending, (state) => {
        state.fetchActionTypesStatus = "loading";
      })
      .addCase(fetchAdminActionTypes.fulfilled, (state, action) => {
        state.fetchActionTypesStatus = "succeeded";
        state.actionTypes = action.payload;
      })
      .addCase(fetchAdminActionTypes.rejected, (state, action) => {
        state.fetchActionTypesStatus = "failed";
        state.error = action.payload || "Failed to fetch admin action types";
      })
      .addCase(fetchAdminActionAdmins.pending, (state) => {
        state.fetchActionAdminsStatus = "loading";
      })
      .addCase(fetchAdminActionAdmins.fulfilled, (state, action) => {
        state.fetchActionAdminsStatus = "succeeded";
        state.admins = action.payload;
      })
      .addCase(fetchAdminActionAdmins.rejected, (state, action) => {
        state.fetchActionAdminsStatus = "failed";
        state.error = action.payload || "Failed to fetch admin action admins";
      })
      .addCase(fetchAdminActionTournaments.pending, (state) => {
        state.fetchActionTournamentsStatus = "loading";
      })
      .addCase(fetchAdminActionTournaments.fulfilled, (state, action) => {
        state.fetchActionTournamentsStatus = "succeeded";
        const nextItems = Array.isArray(action.payload?.items)
          ? action.payload.items
          : [];
        const nextPagination = action.payload?.pagination || {};
        const requestedPage = Number(action.meta?.arg?.page || 1);

        if (requestedPage > 1) {
          const existingIds = new Set(
            state.tournaments.items.map((item) => String(item?._id || "")),
          );
          const merged = [...state.tournaments.items];
          nextItems.forEach((item) => {
            const itemId = String(item?._id || "");
            if (!itemId || existingIds.has(itemId)) return;
            existingIds.add(itemId);
            merged.push(item);
          });
          state.tournaments.items = merged;
        } else {
          state.tournaments.items = nextItems;
        }

        state.tournaments.pagination = {
          ...state.tournaments.pagination,
          ...nextPagination,
        };
      })
      .addCase(fetchAdminActionTournaments.rejected, (state, action) => {
        state.fetchActionTournamentsStatus = "failed";
        state.error = action.payload || "Failed to fetch admin action tournaments";
      });
  },
});

export default adminActionLogsSlice.reducer;
